<script setup lang="ts">

import { Calendar, CalendarCheck2, User } from 'lucide-vue-next';
import { getParam, convertServerDateToLocalDate, bucketTime, isToday } from '@/lib/utils';
import { useRoute } from 'vue-router';
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { toast } from '@/components/ui/toast';
import { store } from '@/store/store';
import { Button } from '@/components/ui/button';
import SupportChat from '@/components/shared/SupportChat.vue';

const route = useRoute();
const loader = ref(true);
const data = ref({} as any);
const d1 = ref('');
const d2 = ref('');
const d3 = ref('');
const later = ref('');

async function getAppt() {
    try {
        const res = await axios.post('/getAppointment', { appID: route.query.appID }) as any;
        if (res.status == 'success') {
            const safeAppData = JSON.parse(JSON.stringify(res.data));
            data.value = safeAppData;

            store.setBookedApp(safeAppData);
            setInfo(res.data.time);
        } else {
            toast({
                title: res.msg ?? 'An error occurred while fetching your appointment info. Please reload the page.',
                variant: 'destructive'
            });
            loader.value = false;
        }
    } catch {
        toast({
            title: 'An error occurred while fetching your appointment info. Please reload the page.',
            variant: 'destructive'
        });
        loader.value = false;
    }
}

function setInfo(time: string) {
    const date = convertServerDateToLocalDate(`${data.value.day} ${time}`);
    if (isToday(date)) {
        d1.value = 'Today'
    } else {
        d1.value = date.toLocaleString('en-US', { weekday: 'short' })
    }

    d2.value = date.toLocaleString('en-US', { month: 'short', day: 'numeric' })
    d3.value = date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric' })
    data.value.time = bucketTime(d3.value.split(' ')[0]);

    if (data.value.status == 'WinRx') {

        let diff = diff_hours(date, new Date());

        if (diff < 24) {
            later.value = ` (${diff} hours later).`;
        }

    }

    loader.value = false;
            
}

function diff_hours(dt2, dt1) {

    let diff =(dt2.getTime() - dt1.getTime()) / 1000;
    diff /= (60 * 60);

    return Math.abs(Math.round(diff));

}

onMounted(() => {
    getAppt();
});
</script>

<template>
    <Loader2 v-if="loader" class="animate-spin w-10 h-10 mx-auto mt-10"/>
    <div v-else class="mt-32">
        <CalendarCheck2 class="w-10 h-10 text-gray-900 mx-auto" />
        <h1 class="text-gray-900 text-4xl font-bold mt-4 text-center">You already have an appointment</h1>
        <div class="mt-2 text-lg text-gray-700 text-center">You can book a new one after it’s<br> completed or cancelled.</div>
        <div class="my-12">
            <div class="flex items-center">
                <div class="border rounded-full border-gray-200 p-2.5 mr-3">
                    <Calendar class="w-5 h-5" />
                </div>
                <div>
                    <div class="text-gray-500 text-sm">Appointment</div>
                    <div class="mt-1 text-gray-900">
                        <span class="font-semibold">{{ d1 + ', ' + d2 + ', ' + d3 }}</span>
                        <span>{{ later }}</span>
                    </div>
                </div>
            </div>
            <div class="flex items-center mt-5">
                <img v-if="data.doctor?.avatar" :src="data.doctor?.avatar" :alt="data.doctor?.name" class="mr-3 w-10 h-10 rounded-full">
                <div v-else class="border rounded-full border-gray-200 p-2.5 mr-3">
                    <User class="w-5 h-5" />
                </div>
                <div>
                    <div class="text-gray-500 text-sm">Doctor</div>
                    <div class="mt-1 text-gray-900 font-semibold">{{ data.doctor?.name }}</div>
                </div>
            </div>
        </div>
        <Button size="lg" as-child class="w-full">
            <RouterLink :to="{ name: 'manage_appointment', params: { domain: getParam(route) }, query: { appID: route.query.appID } }">Modify Appointment</RouterLink>
        </Button>
        <div class="font-semibold text-gray-900 mt-5">
            Need help? <SupportChat />
        </div>
    </div>
</template>
